import { createContext, useContext, useState, useEffect } from 'react';

// Translations
const translations = {
  en: {
    // Navigation
    nav: {
      home: "Home",
      dashboard: "Dashboard",
      subscription: "Subscription",
      profiles: "Profiles",
      admin: "Admin",
      login: "Login",
      logout: "Logout"
    },
    // Landing Page
    landing: {
      heroTitle: "Ask anything about your future —",
      heroTitleHighlight: "powered by Nakshatra AI",
      heroSubtitle: "Horoscope • Kundli Matching • Instant Answers",
      ctaButton: "Start Free Reading",
      trustLine: "100% private • Based on Vedic Astrology",
      features: "Features",
      pricing: "Pricing",
      howItWorks: "How It Works",
      downloadApp: "Download App",
      app: "App",
      getStarted: "Get Started",
      tryDemo: "Try Demo",
      installNakshatra: "Install Nakshatra",
      addToHomeDesc: "Add to your home screen for quick access",
      tapShare: "Tap the Share button",
      atBottomSafari: "At the bottom of Safari",
      selectAddHome: "Select \"Add to Home Screen\"",
      scrollShareMenu: "Scroll down in the share menu",
      tapAdd: "Tap \"Add\"",
      willAppearHome: "Nakshatra will appear on your home screen",
      gotIt: "Got it!"
    },
    // Dashboard
    dashboard: {
      welcome: "Welcome back — your astrology insights are ready",
      welcomeSubtitle: "Ask anything, explore your horoscope, or check compatibility",
      selectProfile: "Select Profile",
      manage: "Manage",
      addProfile: "Add",
      // Feature Cards
      horoscope: "Your Horoscope",
      horoscopeDesc: "Daily, Weekly & Monthly",
      horoscopeInfo: "Personalized predictions based on your birth chart",
      janamKundli: "Janam Kundli",
      janamKundliDesc: "Birth Chart & PDF",
      janamKundliInfo: "Generate your complete Janam Patrika with PDF download",
      kundliMatching: "Kundli Matching",
      kundliMatchingDesc: "Marriage Compatibility",
      kundliMatchingInfo: "36-point Ashtakoot Guna Milan with PDF",
      askAstrologer: "Ask Astrologer",
      askAstrologerDesc: "AI-Powered Guidance",
      askAstrologerInfo: "Get instant answers about career, love & finance",
      // Tabs
      today: "Today",
      weekly: "Weekly",
      monthly: "Monthly"
    },
    // AI Chat
    chat: {
      title: "Ask Your Astrologer",
      subtitle: "Get instant answers about your life, love, career & destiny",
      placeholder: "Ask about your future...",
      listening: "Listening...",
      clickToAsk: "Click to ask:",
      question1: "When will I get married?",
      question2: "Is my career on the right path?",
      question3: "Why am I facing problems?",
      question4: "Will I be financially successful?"
    },
    // Janam Kundli
    kundli: {
      title: "Janam Kundli",
      subtitle: "Birth Chart Generator",
      birthDetails: "Birth Details",
      dateOfBirth: "Date of Birth",
      timeOfBirth: "Time of Birth",
      placeOfBirth: "Place of Birth",
      generateKundli: "Generate Janam Kundli",
      generating: "Generating Kundli...",
      downloadPdf: "Download PDF",
      birthChart: "Birth Charts",
      d1Chart: "D-1 Lagna Chart (Birth Chart)",
      d1Desc: "Main birth chart showing planetary positions at birth",
      d9Chart: "D-9 Navamsa Chart",
      d9Desc: "Divisional chart for marriage & spiritual growth",
      lagna: "Lagna (Ascendant)",
      rashi: "Rashi (Moon Sign)",
      nakshatra: "Nakshatra",
      currentDasha: "Current Dasha Period",
      mahaDasha: "Maha Dasha",
      antarDasha: "Antar Dasha",
      pratyantarDasha: "Pratyantar Dasha",
      planetaryPositions: "Planetary Positions",
      aiInterpretation: "AI Interpretation"
    },
    // Kundli Milan
    milan: {
      title: "Kundli Milan",
      subtitle: "Marriage Compatibility Analysis",
      selectPartner1: "Select First Partner",
      selectPartner2: "Select Second Partner",
      checkCompatibility: "Check Compatibility",
      checking: "Analyzing...",
      downloadReport: "Download PDF Report",
      checkAnother: "Check Another Pair",
      totalScore: "Total Score",
      outOf: "out of",
      points: "points",
      compatibility: "Compatibility"
    },
    // Subscription
    subscription: {
      title: "Choose Your Plan",
      subtitle: "Unlock deeper cosmic insights",
      free: "Free",
      stellar: "Stellar",
      cosmic: "Cosmic",
      popular: "Popular",
      currentPlan: "Current Plan",
      upgrade: "Upgrade",
      subscribe: "Subscribe",
      perMonth: "/month",
      features: {
        profiles: "profiles",
        dailyPredictions: "Daily predictions",
        weeklyForecasts: "Weekly forecasts",
        monthlyForecasts: "Monthly forecasts",
        detailedRemedies: "Detailed remedies",
        prioritySupport: "Priority support",
        gemstoneRec: "Gemstone recommendations"
      }
    },
    // Profiles
    profiles: {
      title: "Manage Profiles",
      subtitle: "Add family members for their personalized predictions",
      addNew: "Add New Profile",
      edit: "Edit",
      delete: "Delete",
      name: "Name",
      dateOfBirth: "Date of Birth",
      timeOfBirth: "Time of Birth",
      placeOfBirth: "Place of Birth",
      save: "Save Profile",
      cancel: "Cancel"
    },
    // Common
    common: {
      loading: "Loading...",
      error: "Something went wrong",
      retry: "Try Again",
      back: "Back",
      next: "Next",
      submit: "Submit",
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      edit: "Edit",
      view: "View",
      download: "Download",
      share: "Share",
      installApp: "Install App",
      addToHomeScreen: "Add to Home Screen",
      selectProfile: "Select Profile",
      manage: "Manage",
      add: "Add",
      lagna: "Lagna",
      moonSign: "Moon Sign",
      nakshatra: "Nakshatra",
      mahadasha: "Mahadasha",
      antardasha: "Antardasha",
      luckyColor: "Lucky Color",
      luckyNumber: "Lucky Number",
      todaysPrediction: "Today's Prediction",
      weeklyPrediction: "Weekly Prediction",
      monthlyPrediction: "Monthly Prediction",
      overview: "Overview",
      career: "Career",
      relationships: "Relationships",
      health: "Health",
      remedies: "Remedies",
      generatePrediction: "Generate Prediction",
      noPrediction: "No prediction available",
      viewDetails: "View Details",
      comingSoon: "Coming Soon",
      playStore: "Google Play Store",
      installFromBrowser: "Install from Browser",
      quickInstall: "Quick install via Chrome menu",
      chooseInstallMethod: "Choose your preferred installation method"
    },
    // Palm Reading
    palmReading: {
      title: "Palm Reading",
      subtitle: "AI-Powered Palmistry Analysis",
      uploadTitle: "Upload Your Palm Image",
      uploadDesc: "Take a clear photo of your palm in good lighting",
      captureBtn: "Capture Palm",
      uploadBtn: "Upload from Gallery",
      analyzing: "Analyzing your palm lines...",
      results: "Your Palm Reading Results",
      lifePath: "Life Path",
      career: "Career & Success",
      love: "Love & Marriage",
      health: "Health & Vitality",
      wealth: "Wealth & Fortune",
      newReading: "New Reading"
    },
    // Yearly Predictions
    yearly: {
      title: "Yearly Predictions",
      subtitle: "Your Complete Forecast",
      overview: "Year Overview",
      monthlyHighlights: "Monthly Highlights",
      categories: {
        career: "Career",
        love: "Love & Relationships",
        health: "Health",
        wealth: "Wealth & Finance",
        family: "Family",
        spiritual: "Spiritual Growth"
      },
      luckyElements: "Lucky Elements",
      luckyColor: "Lucky Color",
      luckyNumber: "Lucky Number",
      luckyDays: "Lucky Days",
      luckyGemstone: "Lucky Gemstone"
    },
    // Family Compatibility
    familyCompatibility: {
      title: "Family Compatibility",
      subtitle: "Analyze family relationships",
      selectMembers: "Select Family Members",
      member1: "First Member",
      member2: "Second Member",
      analyze: "Analyze Compatibility",
      overallScore: "Overall Score",
      aspects: {
        mental: "Mental Harmony",
        emotional: "Emotional Bond",
        spiritual: "Spiritual Connection",
        karmic: "Karmic Link",
        communication: "Communication",
        support: "Mutual Support"
      }
    },
    // Admin
    admin: {
      title: "Admin Dashboard",
      subtitle: "Manage users, payments & analytics",
      totalUsers: "Total Users",
      activeSubscriptions: "Active Subscriptions",
      totalRevenue: "Total Revenue",
      totalProfiles: "Total Profiles",
      trafficAnalytics: "Traffic Analytics",
      todayVisits: "Today's Visits",
      weeklyVisits: "Weekly Visits",
      todayLogins: "Today's Logins",
      weeklyLogins: "Weekly Logins",
      overview: "Overview",
      users: "Users",
      payments: "Payments"
    }
  },
  hi: {
    // Navigation
    nav: {
      home: "होम",
      dashboard: "डैशबोर्ड",
      subscription: "सदस्यता",
      profiles: "प्रोफाइल",
      admin: "एडमिन",
      login: "लॉगिन",
      logout: "लॉगआउट"
    },
    // Landing Page
    landing: {
      heroTitle: "अपने भविष्य के बारे में कुछ भी पूछें —",
      heroTitleHighlight: "नक्षत्र AI द्वारा संचालित",
      heroSubtitle: "राशिफल • कुंडली मिलान • तुरंत जवाब",
      ctaButton: "मुफ्त पढ़ना शुरू करें",
      trustLine: "100% निजी • वैदिक ज्योतिष पर आधारित",
      features: "विशेषताएं",
      pricing: "मूल्य निर्धारण",
      howItWorks: "कैसे काम करता है",
      downloadApp: "ऐप डाउनलोड करें",
      app: "ऐप",
      getStarted: "शुरू करें",
      tryDemo: "डेमो देखें",
      installNakshatra: "नक्षत्र इंस्टॉल करें",
      addToHomeDesc: "त्वरित पहुंच के लिए होम स्क्रीन में जोड़ें",
      tapShare: "Share बटन टैप करें",
      atBottomSafari: "Safari के नीचे",
      selectAddHome: "\"होम स्क्रीन में जोड़ें\" चुनें",
      scrollShareMenu: "शेयर मेनू में नीचे स्क्रॉल करें",
      tapAdd: "\"जोड़ें\" टैप करें",
      willAppearHome: "नक्षत्र आपकी होम स्क्रीन पर दिखाई देगा",
      gotIt: "समझ गया!"
    },
    // Dashboard
    dashboard: {
      welcome: "स्वागत है — आपकी ज्योतिष जानकारी तैयार है",
      welcomeSubtitle: "कुछ भी पूछें, अपना राशिफल देखें, या अनुकूलता जांचें",
      selectProfile: "प्रोफ़ाइल चुनें",
      manage: "प्रबंधित करें",
      addProfile: "जोड़ें",
      // Feature Cards
      horoscope: "आपका राशिफल",
      horoscopeDesc: "दैनिक, साप्ताहिक और मासिक",
      horoscopeInfo: "आपकी जन्म कुंडली पर आधारित भविष्यवाणियां",
      janamKundli: "जन्म कुंडली",
      janamKundliDesc: "जन्म पत्रिका और PDF",
      janamKundliInfo: "PDF डाउनलोड के साथ पूर्ण जन्म पत्रिका बनाएं",
      kundliMatching: "कुंडली मिलान",
      kundliMatchingDesc: "विवाह अनुकूलता",
      kundliMatchingInfo: "PDF के साथ 36 गुण अष्टकूट मिलान",
      askAstrologer: "ज्योतिषी से पूछें",
      askAstrologerDesc: "AI-संचालित मार्गदर्शन",
      askAstrologerInfo: "करियर, प्यार और वित्त के बारे में तुरंत जवाब पाएं",
      // Tabs
      today: "आज",
      weekly: "साप्ताहिक",
      monthly: "मासिक"
    },
    // AI Chat
    chat: {
      title: "ज्योतिषी से पूछें",
      subtitle: "जीवन, प्यार, करियर और भाग्य के बारे में तुरंत जवाब पाएं",
      placeholder: "अपने भविष्य के बारे में पूछें...",
      listening: "सुन रहा है...",
      clickToAsk: "पूछने के लिए क्लिक करें:",
      question1: "मेरी शादी कब होगी?",
      question2: "क्या मेरा करियर सही रास्ते पर है?",
      question3: "मुझे समस्याएं क्यों आ रही हैं?",
      question4: "क्या मैं आर्थिक रूप से सफल होऊंगा?"
    },
    // Janam Kundli
    kundli: {
      title: "जन्म कुंडली",
      subtitle: "जन्म पत्रिका जनरेटर",
      birthDetails: "जन्म विवरण",
      dateOfBirth: "जन्म तिथि",
      timeOfBirth: "जन्म समय",
      placeOfBirth: "जन्म स्थान",
      generateKundli: "जन्म कुंडली बनाएं",
      generating: "कुंडली बना रहे हैं...",
      downloadPdf: "PDF डाउनलोड करें",
      birthChart: "जन्म कुंडली",
      d1Chart: "D-1 लग्न कुंडली (जन्म कुंडली)",
      d1Desc: "जन्म के समय ग्रहों की स्थिति दिखाने वाली मुख्य कुंडली",
      d9Chart: "D-9 नवांश कुंडली",
      d9Desc: "विवाह और आध्यात्मिक विकास के लिए विभाजन कुंडली",
      lagna: "लग्न (उदय राशि)",
      rashi: "राशि (चंद्र राशि)",
      nakshatra: "नक्षत्र",
      currentDasha: "वर्तमान दशा काल",
      mahaDasha: "महा दशा",
      antarDasha: "अंतर दशा",
      pratyantarDasha: "प्रत्यंतर दशा",
      planetaryPositions: "ग्रह स्थिति",
      aiInterpretation: "AI व्याख्या"
    },
    // Kundli Milan
    milan: {
      title: "कुंडली मिलान",
      subtitle: "विवाह अनुकूलता विश्लेषण",
      selectPartner1: "पहला साथी चुनें",
      selectPartner2: "दूसरा साथी चुनें",
      checkCompatibility: "अनुकूलता जांचें",
      checking: "विश्लेषण हो रहा है...",
      downloadReport: "PDF रिपोर्ट डाउनलोड करें",
      checkAnother: "दूसरी जोड़ी जांचें",
      totalScore: "कुल स्कोर",
      outOf: "में से",
      points: "अंक",
      compatibility: "अनुकूलता"
    },
    // Subscription
    subscription: {
      title: "अपना प्लान चुनें",
      subtitle: "गहरी ब्रह्मांडीय अंतर्दृष्टि प्राप्त करें",
      free: "मुफ्त",
      stellar: "स्टेलर",
      cosmic: "कॉस्मिक",
      popular: "लोकप्रिय",
      currentPlan: "वर्तमान प्लान",
      upgrade: "अपग्रेड",
      subscribe: "सदस्यता लें",
      perMonth: "/महीना",
      features: {
        profiles: "प्रोफाइल",
        dailyPredictions: "दैनिक भविष्यवाणियां",
        weeklyForecasts: "साप्ताहिक पूर्वानुमान",
        monthlyForecasts: "मासिक पूर्वानुमान",
        detailedRemedies: "विस्तृत उपाय",
        prioritySupport: "प्राथमिकता सहायता",
        gemstoneRec: "रत्न सिफारिशें"
      }
    },
    // Profiles
    profiles: {
      title: "प्रोफाइल प्रबंधित करें",
      subtitle: "परिवार के सदस्यों को उनकी व्यक्तिगत भविष्यवाणियों के लिए जोड़ें",
      addNew: "नई प्रोफाइल जोड़ें",
      edit: "संपादित करें",
      delete: "हटाएं",
      name: "नाम",
      dateOfBirth: "जन्म तिथि",
      timeOfBirth: "जन्म समय",
      placeOfBirth: "जन्म स्थान",
      save: "प्रोफाइल सहेजें",
      cancel: "रद्द करें"
    },
    // Common
    common: {
      loading: "लोड हो रहा है...",
      error: "कुछ गलत हो गया",
      retry: "पुनः प्रयास करें",
      back: "वापस",
      next: "अगला",
      submit: "जमा करें",
      save: "सहेजें",
      cancel: "रद्द करें",
      delete: "हटाएं",
      edit: "संपादित करें",
      view: "देखें",
      download: "डाउनलोड",
      share: "शेयर करें",
      installApp: "ऐप इंस्टॉल करें",
      addToHomeScreen: "होम स्क्रीन में जोड़ें",
      selectProfile: "प्रोफ़ाइल चुनें",
      manage: "प्रबंधित करें",
      add: "जोड़ें",
      lagna: "लग्न",
      moonSign: "चंद्र राशि",
      nakshatra: "नक्षत्र",
      mahadasha: "महादशा",
      antardasha: "अंतरदशा",
      luckyColor: "शुभ रंग",
      luckyNumber: "शुभ अंक",
      todaysPrediction: "आज की भविष्यवाणी",
      weeklyPrediction: "साप्ताहिक भविष्यवाणी",
      monthlyPrediction: "मासिक भविष्यवाणी",
      overview: "अवलोकन",
      career: "करियर",
      relationships: "रिश्ते",
      health: "स्वास्थ्य",
      remedies: "उपाय",
      generatePrediction: "भविष्यवाणी बनाएं",
      noPrediction: "कोई भविष्यवाणी उपलब्ध नहीं",
      viewDetails: "विवरण देखें",
      comingSoon: "जल्द आ रहा है",
      playStore: "गूगल प्ले स्टोर",
      installFromBrowser: "ब्राउज़र से इंस्टॉल करें",
      quickInstall: "Chrome मेनू से त्वरित इंस्टॉल",
      chooseInstallMethod: "अपनी पसंदीदा इंस्टॉल विधि चुनें"
    },
    // Palm Reading
    palmReading: {
      title: "हस्तरेखा पढ़ना",
      subtitle: "AI-संचालित हस्तरेखा विश्लेषण",
      uploadTitle: "अपनी हथेली की तस्वीर अपलोड करें",
      uploadDesc: "अच्छी रोशनी में अपनी हथेली की स्पष्ट तस्वीर लें",
      captureBtn: "हथेली कैप्चर करें",
      uploadBtn: "गैलरी से अपलोड करें",
      analyzing: "आपकी हस्तरेखाओं का विश्लेषण हो रहा है...",
      results: "आपके हस्तरेखा पढ़ने के परिणाम",
      lifePath: "जीवन पथ",
      career: "करियर और सफलता",
      love: "प्यार और विवाह",
      health: "स्वास्थ्य और जीवन शक्ति",
      wealth: "धन और भाग्य",
      newReading: "नया पढ़ना"
    },
    // Yearly Predictions
    yearly: {
      title: "वार्षिक भविष्यवाणी",
      subtitle: "आपका संपूर्ण पूर्वानुमान",
      overview: "वर्ष का अवलोकन",
      monthlyHighlights: "मासिक मुख्य बातें",
      categories: {
        career: "करियर",
        love: "प्यार और रिश्ते",
        health: "स्वास्थ्य",
        wealth: "धन और वित्त",
        family: "परिवार",
        spiritual: "आध्यात्मिक विकास"
      },
      luckyElements: "शुभ तत्व",
      luckyColor: "शुभ रंग",
      luckyNumber: "शुभ अंक",
      luckyDays: "शुभ दिन",
      luckyGemstone: "शुभ रत्न"
    },
    // Family Compatibility
    familyCompatibility: {
      title: "पारिवारिक अनुकूलता",
      subtitle: "पारिवारिक संबंधों का विश्लेषण करें",
      selectMembers: "परिवार के सदस्यों का चयन करें",
      member1: "पहला सदस्य",
      member2: "दूसरा सदस्य",
      analyze: "अनुकूलता का विश्लेषण करें",
      overallScore: "कुल स्कोर",
      aspects: {
        mental: "मानसिक सामंजस्य",
        emotional: "भावनात्मक बंधन",
        spiritual: "आध्यात्मिक संबंध",
        karmic: "कार्मिक संबंध",
        communication: "संवाद",
        support: "परस्पर सहयोग"
      }
    },
    // Admin
    admin: {
      title: "एडमिन डैशबोर्ड",
      subtitle: "उपयोगकर्ता, भुगतान और विश्लेषण प्रबंधित करें",
      totalUsers: "कुल उपयोगकर्ता",
      activeSubscriptions: "सक्रिय सदस्यताएं",
      totalRevenue: "कुल राजस्व",
      totalProfiles: "कुल प्रोफाइल",
      trafficAnalytics: "ट्रैफिक विश्लेषण",
      todayVisits: "आज की विज़िट",
      weeklyVisits: "साप्ताहिक विज़िट",
      todayLogins: "आज के लॉगिन",
      weeklyLogins: "साप्ताहिक लॉगिन",
      overview: "अवलोकन",
      users: "उपयोगकर्ता",
      payments: "भुगतान"
    }
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage for saved preference
    if (typeof window !== 'undefined') {
      return localStorage.getItem('nakshatra_lang') || 'en';
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('nakshatra_lang', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'hi' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
